import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'

interface HorizontalScrollbarProps {
    scrollRef: React.RefObject<HTMLDivElement>
}

/**
 * Always-visible custom horizontal scrollbar for tables.
 * Native scrollbars auto-hide on macOS regardless of overflow setting; this
 * renders a persistent track + draggable thumb so non-touchpad users see and
 * can use horizontal scrolling.
 */
export const HorizontalScrollbar = ({
    scrollRef,
}: HorizontalScrollbarProps) => {
    const trackRef = useRef<HTMLDivElement>(null)
    const thumbRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(
        null
    )
    const [isOverflowing, setIsOverflowing] = useState(false)

    const update = useCallback(() => {
        const scrollEl = scrollRef.current
        const trackEl = trackRef.current
        const thumbEl = thumbRef.current
        if (!scrollEl) return

        const { scrollWidth, clientWidth, scrollLeft } = scrollEl
        const overflows = scrollWidth > clientWidth + 1
        setIsOverflowing(overflows)

        if (!overflows || !trackEl || !thumbEl) return

        const trackWidth = trackEl.clientWidth
        const ratio = clientWidth / scrollWidth
        const thumbWidth = Math.max(40, Math.floor(trackWidth * ratio))
        const maxScroll = scrollWidth - clientWidth
        const maxThumbOffset = trackWidth - thumbWidth
        const thumbOffset =
            maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbOffset : 0

        thumbEl.style.width = `${thumbWidth}px`
        thumbEl.style.transform = `translateX(${thumbOffset}px)`
    }, [scrollRef])

    useEffect(() => {
        const scrollEl = scrollRef.current
        if (!scrollEl) return

        update()
        const ro = new ResizeObserver(update)
        ro.observe(scrollEl)
        const inner = scrollEl.firstElementChild
        if (inner) ro.observe(inner as Element)

        scrollEl.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)

        return () => {
            ro.disconnect()
            scrollEl.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [update, scrollRef])

    // After the track/thumb mount (isOverflowing flipped to true), measure and
    // size the thumb immediately. Without this, the thumb has no width on its
    // first paint and visually fills the entire track until the next event.
    useLayoutEffect(() => {
        if (!isOverflowing) return
        update()
    }, [isOverflowing, update])

    const onThumbDown = (e: React.PointerEvent) => {
        const scrollEl = scrollRef.current
        if (!scrollEl) return
        e.preventDefault()
        e.stopPropagation()
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        dragRef.current = {
            startX: e.clientX,
            startScrollLeft: scrollEl.scrollLeft,
        }
    }

    const onThumbMove = (e: React.PointerEvent) => {
        const drag = dragRef.current
        const scrollEl = scrollRef.current
        const trackEl = trackRef.current
        const thumbEl = thumbRef.current
        if (!drag || !scrollEl || !trackEl || !thumbEl) return

        const trackWidth = trackEl.clientWidth
        const thumbWidth = thumbEl.clientWidth
        const maxThumbOffset = trackWidth - thumbWidth
        const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth
        if (maxThumbOffset <= 0 || maxScroll <= 0) return

        const dx = e.clientX - drag.startX
        scrollEl.scrollLeft =
            drag.startScrollLeft + dx * (maxScroll / maxThumbOffset)
    }

    const onThumbUp = (e: React.PointerEvent) => {
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        dragRef.current = null
    }

    const onTrackDown = (e: React.PointerEvent) => {
        const scrollEl = scrollRef.current
        const trackEl = trackRef.current
        const thumbEl = thumbRef.current
        if (!scrollEl || !trackEl || !thumbEl) return
        if (e.target === thumbEl) return

        const rect = trackEl.getBoundingClientRect()
        const thumbWidth = thumbEl.clientWidth
        const x = e.clientX - rect.left - thumbWidth / 2
        const maxThumbOffset = trackEl.clientWidth - thumbWidth
        const clamped = Math.max(0, Math.min(maxThumbOffset, x))
        const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth
        scrollEl.scrollTo({
            left: (clamped / maxThumbOffset) * maxScroll,
            behavior: 'smooth',
        })
    }

    if (!isOverflowing) return null

    return (
        <div
            ref={trackRef}
            className="table-x-scrollbar-track"
            onPointerDown={onTrackDown}
        >
            <div
                ref={thumbRef}
                className="table-x-scrollbar-thumb"
                onPointerDown={onThumbDown}
                onPointerMove={onThumbMove}
                onPointerUp={onThumbUp}
                onPointerCancel={onThumbUp}
            />
        </div>
    )
}
